import type { Role } from '@cmoh/shared'
import { hasAtLeast as hasAtLeastRole, ROLE } from '@cmoh/shared'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthContext } from '@/features/auth/hooks/use-auth-context'
import { useIdentifyUser } from '@/features/auth/hooks/use-identify-user'
import { useOnboardingRedirect } from '@/features/auth/hooks/use-onboarding-redirect'

type BaseGateProps = {
  children: ReactNode
  loadingFallback?: ReactNode
  unauthenticatedFallback?: ReactNode
  unauthorizedFallback?: ReactNode
  superAdminOnly?: boolean
  superAdminBypass?: boolean
}

type GateByAllowedRoles = BaseGateProps & {
  roles: Readonly<Role[]>
  minRole?: never
}

type GateByMinRole = BaseGateProps & {
  roles?: never
  minRole: Role
}

/**
 * Evaluation order:
 *  1) Not authenticated → unauthenticatedFallback or /login
 *  2) superAdminOnly → requires isSuperAdmin
 *  3) isSuperAdmin && superAdminBypass !== false → allow
 *  4) roles → allow if includes userRole
 *  5) minRole → allow if userRole >= minRole
 *  6) No restrictions → allow
 */
export function PermissionGate(props: GateByAllowedRoles | GateByMinRole) {
  const {
    children,
    loadingFallback = null,
    unauthenticatedFallback,
    unauthorizedFallback,
    superAdminOnly = false,
    superAdminBypass = true,
  } = props

  const location = useLocation()
  const { user, membership, isLoading } = useAuthContext()

  // Check onboarding status and redirect if necessary
  useOnboardingRedirect()
  useIdentifyUser()

  if (isLoading) return <>{loadingFallback}</>

  const isAuthenticated = Boolean(user)

  // Development bypass: skip authentication when VITE_SKIP_AUTH is true
  const skipAuth = import.meta.env.VITE_SKIP_AUTH === 'true' && import.meta.env.DEV

  if (!isAuthenticated && !skipAuth) {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('next', location.pathname + location.search)
    return (
      <>
        {unauthenticatedFallback ?? <Navigate to={`/login?${searchParams.toString()}`} replace />}
      </>
    )
  }

  // When bypassing auth in development, use mock user data
  const userRole = skipAuth ? ROLE.ADMIN : membership?.role
  const isSuperAdmin = skipAuth ? true : Boolean(user?.isSuperAdmin)

  if (superAdminOnly) {
    return <>{isSuperAdmin ? children : (unauthorizedFallback ?? <Navigate to="/403" replace />)}</>
  }

  if (isSuperAdmin && superAdminBypass) {
    return <>{children}</>
  }

  if (!userRole) {
    // User authenticated without defined role => treat it as without permissions.
    return <>{unauthorizedFallback ?? <Navigate to="/403" replace />}</>
  }

  // 1) Roles Whitelist
  if ('roles' in props && props.roles && props.roles.length > 0) {
    const ok = props.roles.includes(userRole)
    return <>{ok ? children : (unauthorizedFallback ?? <Navigate to="/403" replace />)}</>
  }

  // 2) Minimum role (hierarchy)
  if ('minRole' in props && props.minRole) {
    const ok = hasAtLeastRole(userRole, props.minRole)
    return <>{ok ? children : (unauthorizedFallback ?? <Navigate to="/403" replace />)}</>
  }

  return <>{children}</>
}
